import React, { useState, useEffect, createRef, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useIntl } from 'react-intl';
import ReCAPTCHA from 'react-google-recaptcha';

import { RecaptchaSize } from '../../types';
import cachedApi from '../../utils/getCachedRequest';
import { CombinatorSectionItem } from '../../ContentfulTypes';

import { FormFieldT, FormData, FormLayoutProps } from './types';
import FormLayout from './FormLayout';

interface Props<T extends FormLayoutProps> {
  formFields: FormFieldT[];
  onSubmit: (args: unknown) => void;
  successSection?: CombinatorSectionItem;
  generalError?: CombinatorSectionItem;
  conflictError?: CombinatorSectionItem;
  renderSuccessCta?: () => ReactElement;
  recaptchaSize?: RecaptchaSize;
  validationSchema?: any;
  formData?: FormData | null;
  setFormData?: React.Dispatch<React.SetStateAction<FormData | null | undefined>>;
  error?: string;
  loading?: boolean;
  testId?: string;
  Layout?: (args: T) => React.JSX.Element;
  layoutProps?: any;
  dropdownData?: Record<string, string>;
  setDropdownData?: React.Dispatch<
    React.SetStateAction<Record<string, string> | undefined>
  >;
  preselectedValues?: URLSearchParams;
}

const Form = <T extends FormLayoutProps, >({
  formFields,
  successSection,
  generalError,
  renderSuccessCta,
  recaptchaSize = RecaptchaSize.NORMAL,
  validationSchema,
  onSubmit,
  error,
  loading,
  formData,
  setFormData,
  testId,
  Layout = FormLayout,
  layoutProps,
  dropdownData,
  setDropdownData,
  preselectedValues,
  conflictError,
}: Props<T>) => {
  const { locale } = useIntl();

  // Set status of the form
  const [stage, setStage] = useState('form');
  // Set field value based on user input
  const [customerOptIn, setcustomerOptIn] = useState(false);
  // Define ReCaptcha Value
  const recaptchaRef = createRef<ReCAPTCHA>();

  const [activeDropdown, setActiveDropdown] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (customerOptIn) {
      setcustomerOptIn(!customerOptIn);
    }
  }, [customerOptIn]);

  useEffect(() => {
    if (loading) {
      setStage('loading');
    } else if (formData !== null && error !== '') {
      // Error view
      setStage('form');
    } else if (formData !== null && error === '') {
      setStage('submitted');
    }
  }, [loading, formData, error]);

  if (typeof window !== 'undefined') {
    // @ts-ignore TS does not know of this runtime property
    window.recaptchaOptions = {
      lang: locale,
      useRecaptchaNet: true,
      removeOnUnmount: true,
    };
  }

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, touchedFields, isValid },
    clearErrors,
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const resubmitForm = () => {
    // Temporarily change formData to force `useTmsApi` hook to retrigger
    // Will be set back to form data via form
    if (setFormData) setFormData(null);
    // Reset login cache in case it was cached
    cachedApi.resetCache('auth-login');
    if (recaptchaRef.current) recaptchaRef.current?.reset();
  };

  const submitForm = (e) => {
    handleSubmit(onSubmit)(e);
  };

  const onDropdownChange = (fieldName: string, option: string) => {
    if (setDropdownData) {
      setDropdownData({
        ...dropdownData,
        [fieldName]: option,
      });
    }
  };

  useEffect(() => {
    setActiveDropdown(undefined);
  }, [dropdownData]);

  return (
    <Layout
      submitForm={submitForm}
      formFields={formFields}
      stage={stage}
      clearErrors={clearErrors}
      resubmitForm={resubmitForm}
      register={register}
      control={control}
      recaptchaRef={recaptchaRef}
      recaptchaSize={recaptchaSize}
      isValid={isValid}
      loading={loading}
      generalError={generalError}
      conflictError={conflictError}
      errors={errors}
      error={error}
      touchedFields={touchedFields}
      successSection={successSection}
      testId={testId}
      renderSuccessCta={renderSuccessCta}
      reset={reset}
      onDropdownChange={onDropdownChange}
      dropdownData={dropdownData}
      activeDropdown={activeDropdown}
      setActiveDropdown={setActiveDropdown}
      preselectedValues={preselectedValues}
      {...layoutProps}
    />
  );
};

export default Form;
